/**
 * The edits of the banner editor. All pure: each returns a new banner or a
 * new list and leaves its input as it was. A refused edit returns the
 * sentence to show instead. The editor calls them when a text field is left,
 * never on each keystroke.
 */

import { groupBasis } from "../../compiler/banner"
import { nameKey, refusedName } from "../../compiler/names"
import { groupWords, nestGroups } from "../../model/nest"
import type { RangeItem } from "../../model/ranges"
import { RAW_NAMES, ShownNames } from "../../model/shownLabels"
import {
  Catalogue,
  CatalogueQuestion,
  QuestionSource,
  QuestionType,
} from "../../types/catalogue"
import {
  Banner,
  BannerGroup,
  BannerPoint,
  Condition,
  ConditionGroup,
  isConditionGroup,
  isNoneGroup,
  newTabSpecId,
} from "../../types/tabSpecDocument"

/** A net with no member, in the words the rows tab uses. */
export const NET_NEEDS_MEMBERS = "Choose at least one option."
export const BANNER_NAME_TAKEN = "A banner with that name already exists."
export const LAST_COLUMN_STAYS =
  "A group needs at least one column. Remove the group instead."
export const CONDITION_GROUP_NEEDS_HEADING = "A group of conditions needs a heading."
export const NEST_TWO_GROUPS = "Choose two different groups."
export const NEST_NOTHING = "These groups have no columns to nest."
/** The heading a new group of conditions gets, until it is typed over. */
export const NEW_CONDITION_GROUP = "Conditions"

/** Single and multiple choice questions and grids of them: their answers are columns. */
const CHOICE_TYPES: QuestionType[] = [
  "select-one",
  "select-many",
  "range",
  "grid-one",
  "grid-many",
  "grid-range",
]
/** Numbers and number grids: their columns are ranges. */
const NUMBER_TYPES: QuestionType[] = ["number", "grid-number"]

/**
 * The name a column shows: a category's rename, or its label as shown
 * (`shownOf`, the label itself by default); a net's name.
 */
const shownName = (p: BannerPoint, shownOf: (label: string) => string = (l) => l): string =>
  p.kind === "category" ? p.rename?.trim() || shownOf(p.label) : p.name.trim()

const copyCondition = (c: Condition): Condition =>
  c.map(
    (g): ConditionGroup =>
      isNoneGroup(g)
        ? { none: g.none.map((t) => (typeof t === "string" ? t : { ...t })) }
        : g.map((t) => (typeof t === "string" ? t : { ...t }))
  )

const copyPoint = (p: BannerPoint): BannerPoint =>
  p.kind === "net"
    ? { ...p, members: [...p.members] }
    : p.kind === "condition"
      ? { ...p, condition: copyCondition(p.condition) }
      : { ...p }

const editGroup = (
  banner: Banner,
  groupId: string,
  fn: (g: BannerGroup) => BannerGroup
): Banner => ({
  ...banner,
  groups: banner.groups.map((g) => (g.id === groupId ? fn(g) : g)),
})

/** A question or variable a banner group can be made of, as the question list offers it. */
export interface BannerChoice {
  variable: string
  source?: "data"
  /** The grid row or loop iteration (`CatalogueInstance.id`); absent for a question asked once. */
  instance?: string
  /** As the tables show it: "Recommend", "Recommend - Evrysdi". */
  shown: string
  /** In its own labels: "Recommend - Evrysdi". */
  title: string
}

/**
 * What a banner group can be made of: each single or multiple choice
 * question and each number, and of a grid or a loop each row or iteration
 * on its own ("Recommend - Evrysdi"); once data is loaded, only those in the
 * data. The rule of `compileBanner`, so a choice is never left out of the
 * banner for what it is (a number until it has ranges). The questionnaire's
 * come first, in its order, then the variables of the prepared data under
 * the same rule. Text never.
 */
export const bannerChoices = (
  catalogue: Catalogue,
  names: ShownNames = RAW_NAMES
): BannerChoice[] =>
  [...catalogue.questions, ...(catalogue.dataVariables ?? [])].flatMap((q) => {
    const number = NUMBER_TYPES.includes(q.type)
    if (!number && !CHOICE_TYPES.includes(q.type)) return []
    // A variable nested in the data is named by its path: shown by its parts.
    const shownVariable = names.variable(q.label)
    const raw = q.label.split("//").join(": ")
    const words = shownVariable === q.label ? raw : shownVariable
    const once = q.instances.length === 1
    return q.instances.flatMap((i): BannerChoice[] => {
      if (!number && Object.keys(i.optionPaths).length === 0) return []
      if (catalogue.knownPaths && !i.inData) return []
      const source = q.source === "data" ? { source: "data" as const } : {}
      if (once) return [{ variable: q.label, ...source, shown: words, title: q.label }]
      const row = i.label || i.id
      return [
        {
          variable: q.label,
          ...source,
          instance: i.id,
          shown: `${words} - ${names.instance(q.label, i.id, row)}`,
          title: `${raw} - ${row}`,
        },
      ]
    })
  })

/**
 * The heading of each group as the tables show it, keyed by group id, unique
 * within the banner. A group the compiler takes (`groupBasis`) gets the
 * heading `compileBanner` shows (`groupDisplay`), numbered the same way
 * ("Age band (2)"): its own heading, else its variable's shown label
 * (`names`), else the variable's label, a variable nested in the data by its
 * parts ("Channels: Bank 1"), a grid row or loop iteration after " - ". A
 * group the compiler leaves out gets its words (`groupWords`), numbered
 * after those, so every group can be told apart.
 */
export const groupHeadings = (
  banner: Banner,
  catalogue: Catalogue | null,
  names: ShownNames = RAW_NAMES
): Record<string, string> => {
  const number = (heading: string, taken: Set<string>): string => {
    let header = heading
    for (let n = 2; taken.has(header); n++) header = `${heading} (${n})`
    taken.add(header)
    return header
  }
  const headers = new Set<string>()
  const used: { id: string; header: string; shown: string | null }[] = []
  const left: BannerGroup[] = []
  banner.groups.forEach((g) => {
    const basis = catalogue ? groupBasis(g, catalogue, names) : null
    if (!basis || typeof basis === "string") {
      left.push(g)
      return
    }
    used.push({ id: g.id, header: number(basis.heading, headers), shown: basis.shown })
  })
  // As compileBanner numbers them: an engine heading as it is, a shown label
  // numbered among every heading shown.
  const taken = new Set(used.flatMap((u) => (u.shown === null ? [u.header] : [])))
  const out: Record<string, string> = {}
  used.forEach((u) => {
    out[u.id] = u.shown === null ? u.header : number(u.shown, taken)
  })
  left.forEach((g) => {
    out[g.id] = number(groupWords(g, catalogue, names) || NEW_CONDITION_GROUP, taken)
  })
  return out
}

/**
 * Adds the question as the last group, with every answer as a column (a
 * number gets its ranges in the editor). `source` "data": `variable` is a
 * variable of the prepared data. `instance`: the grid row or loop iteration
 * whose answers the columns are.
 */
export const addGroup = (
  banner: Banner,
  variable: string,
  makeId: () => string = () => newTabSpecId("group"),
  source?: QuestionSource,
  instance?: string
): Banner => ({
  ...banner,
  groups: [
    ...banner.groups,
    {
      id: makeId(),
      variable,
      ...(source === "data" ? { source } : {}),
      ...(instance !== undefined ? { instance } : {}),
      points: null,
    },
  ],
})

/** A name for a new column of `points`: "Column 1", or the next number no column has. */
const newColumnName = (points: BannerPoint[]): string => {
  const names = points.map((p) => nameKey(shownName(p)))
  let n = points.length + 1
  while (names.includes(nameKey(`Column ${n}`))) n++
  return `Column ${n}`
}

/** A condition column with no condition yet, named after its place. */
const newConditionColumn = (points: BannerPoint[]): BannerPoint => ({
  kind: "condition",
  name: newColumnName(points),
  condition: [],
})

/**
 * Adds a group of conditions as the last group: the heading "Conditions"
 * and one column with no condition yet, both to be written in the editor.
 */
export const addConditionGroup = (
  banner: Banner,
  makeId: () => string = () => newTabSpecId("group")
): Banner => ({
  ...banner,
  groups: [
    ...banner.groups,
    { id: makeId(), variable: "", label: NEW_CONDITION_GROUP, points: [newConditionColumn([])] },
  ],
})

/** Appends a condition column with no condition yet ("Column 2"). */
export const addConditionColumn = (points: BannerPoint[]): BannerPoint[] => [
  ...points.map(copyPoint),
  newConditionColumn(points),
]

/**
 * Renames the range or condition column at `index`, trimmed, by the rule for
 * the names of columns (`refusedName`) against every name another column of
 * the group goes by.
 */
export const renameColumn = (
  points: BannerPoint[],
  index: number,
  name: string
): BannerPoint[] | string => {
  const p = points[index]
  if (!p || (p.kind !== "range" && p.kind !== "condition")) return [...points]
  const text = name.trim()
  const others = points.flatMap((q, i) =>
    i === index ? [] : q.kind === "category" ? [q.label, shownName(q)] : [q.name]
  )
  const refused = refusedName(text, others, "column")
  if (refused) return refused
  return points.map((q, i) => (i === index ? { ...copyPoint(q), name: text } : q))
}

/** Sets the condition of the condition column at `index`. */
export const setColumnCondition = (
  points: BannerPoint[],
  index: number,
  condition: Condition
): BannerPoint[] =>
  points.map((p, i) =>
    i === index && p.kind === "condition" ? { ...p, condition: copyCondition(condition) } : p
  )

/** The range columns of `points`, as the range editor lists them. */
export const rangesOf = (points: BannerPoint[] | null): RangeItem[] =>
  (points ?? []).flatMap((p) =>
    p.kind === "range"
      ? [
          {
            name: p.name,
            ...(p.min !== undefined ? { min: p.min } : {}),
            ...(p.max !== undefined ? { max: p.max } : {}),
          },
        ]
      : []
  )

/**
 * The columns of a number group with its ranges as `ranges` and its other
 * columns after them, as they were. null without any column: the group has
 * no ranges yet.
 */
export const withRanges = (
  points: BannerPoint[] | null,
  ranges: RangeItem[]
): BannerPoint[] | null => {
  const next: BannerPoint[] = [
    ...ranges.map(
      (r): BannerPoint => ({
        kind: "range",
        name: r.name,
        ...(r.min !== undefined ? { min: r.min } : {}),
        ...(r.max !== undefined ? { max: r.max } : {}),
      })
    ),
    ...(points ?? []).filter((p) => p.kind !== "range").map(copyPoint),
  ]
  return next.length > 0 ? next : null
}

/**
 * The columns of a number group without the one at `index`, which is not a
 * range: a condition, or an answer or a net that stands for nothing in a
 * number's group. null without any column left: the group has no ranges
 * yet, as `withRanges` gives it.
 */
export const withoutPoint = (points: BannerPoint[], index: number): BannerPoint[] | null => {
  const next = points.filter((_, i) => i !== index).map(copyPoint)
  return next.length > 0 ? next : null
}

/**
 * Appends a group of conditions made of the groups `outerId` by `innerId`
 * (`nestGroups`), and leaves both as they are. Refused for one group twice,
 * and when no pair of columns is left to nest.
 */
export const nestInto = (
  banner: Banner,
  outerId: string,
  innerId: string,
  catalogue: Catalogue,
  names: ShownNames = RAW_NAMES,
  makeId: () => string = () => newTabSpecId("group")
): Banner | string => {
  const outer = banner.groups.find((g) => g.id === outerId)
  const inner = banner.groups.find((g) => g.id === innerId)
  if (!outer || !inner || outerId === innerId) return NEST_TWO_GROUPS
  const nested = nestGroups(outer, inner, catalogue, names, makeId)
  if (!nested.points || nested.points.length === 0) return NEST_NOTHING
  return { ...banner, groups: [...banner.groups, nested] }
}

export const removeGroup = (banner: Banner, groupId: string): Banner => ({
  ...banner,
  groups: banner.groups.filter((g) => g.id !== groupId),
})

/** Moves the group to position `to`, clamped to the list. */
export const moveGroup = (banner: Banner, groupId: string, to: number): Banner => {
  const from = banner.groups.findIndex((g) => g.id === groupId)
  if (from < 0) return { ...banner }
  const groups = [...banner.groups]
  const [moved] = groups.splice(from, 1)
  groups.splice(Math.max(0, Math.min(to, groups.length)), 0, moved)
  return { ...banner, groups }
}

/**
 * Sets the heading of a group, trimmed. An empty heading removes it, so the
 * question label shows again; a heading the rule for shown names
 * (`refusedName`) refuses is refused here. Headings are not compared with
 * each other: the compiler numbers a second one.
 */
export const setGroupLabel = (
  banner: Banner,
  groupId: string,
  label: string
): Banner | string => {
  const text = label.trim()
  // A group of conditions has no question to fall back to.
  if (!text && banner.groups.some((g) => g.id === groupId && isConditionGroup(g)))
    return CONDITION_GROUP_NEEDS_HEADING
  if (!text)
    return editGroup(banner, groupId, (g) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { label: _gone, ...rest } = g
      return rest
    })
  const refused = refusedName(text, [], "column")
  if (refused) return refused
  return editGroup(banner, groupId, (g) => ({ ...g, label: text }))
}

/** The columns as shown, to start editing from: every answer while `points` is null. */
export const materialisePoints = (
  group: BannerGroup,
  q: CatalogueQuestion
): BannerPoint[] =>
  group.points
    ? group.points.map(copyPoint)
    : q.options.map((o) => ({ kind: "category" as const, label: o.label }))

/** null goes back to every answer, in questionnaire order. */
export const setPoints = (
  banner: Banner,
  groupId: string,
  points: BannerPoint[] | null
): Banner =>
  editGroup(banner, groupId, (g) => ({
    ...g,
    points: points ? points.map(copyPoint) : null,
  }))

/**
 * Appends a net. Refused for a name the rule for shown names
 * (`refusedName`) refuses against every name another column goes by (a
 * category's label, which is its name in the engine, and the name it shows;
 * a net's name), and for a net with no member.
 */
export const addBannerNet = (
  points: BannerPoint[],
  name: string,
  members: string[]
): BannerPoint[] | string => {
  const trimmed = name.trim()
  const others = points.flatMap((p) =>
    p.kind === "category" ? [p.label, shownName(p)] : [p.name]
  )
  const refused = refusedName(trimmed, others, "column")
  if (refused) return refused
  if (members.length === 0) return NET_NEEDS_MEMBERS
  return [
    ...points.map(copyPoint),
    { kind: "net", name: trimmed, members: [...members] },
  ]
}

/** Moves the column at `from` to `to`, both clamped to the list. */
export const movePoint = (
  points: BannerPoint[],
  from: number,
  to: number
): BannerPoint[] => {
  const next = [...points]
  if (from < 0 || from >= next.length) return next
  const [moved] = next.splice(from, 1)
  next.splice(Math.max(0, Math.min(to, next.length)), 0, moved)
  return next
}

/**
 * Removes the column at `index`. A group keeps at least one column: the
 * last one is not removed, and the list comes back as it was.
 */
export const removePoint = (points: BannerPoint[], index: number): BannerPoint[] =>
  points.length <= 1 ? [...points] : points.filter((_, i) => i !== index)

/**
 * Renames the category at `index`, trimmed. A blank name, the label itself,
 * or the name it is shown under without a rename (`shownOf`, its shown
 * label; the label itself by default) removes `rename`. The name the column
 * would show goes through the rule for shown names (`refusedName`), against
 * the names the other columns of the group show, their shown labels
 * included; a shown label is only shown, so the engine's character rule does
 * not apply to it. Called when the field is left, so a name typed through
 * the label ("Female" on the way to "Female respondents") is never cut short.
 */
export const renamePoint = (
  points: BannerPoint[],
  index: number,
  rename: string,
  shownOf: (label: string) => string = (l) => l
): BannerPoint[] | string => {
  const p = points[index]
  if (!p || p.kind !== "category") return [...points]
  const text = rename.trim()
  const plain = shownOf(p.label)
  const keep = text !== "" && text !== p.label && text !== plain
  const next: BannerPoint = keep
    ? { kind: "category", label: p.label, rename: text }
    : { kind: "category", label: p.label }
  const others = points.flatMap((q, i) => (i === index ? [] : [shownName(q, shownOf)]))
  const refused = refusedName(
    keep ? text : plain,
    others,
    "column",
    keep || plain === p.label ? "engine" : "display"
  )
  if (refused) return refused
  return points.map((q, i) => (i === index ? next : q))
}

/** Refuses a name another banner of the spec has (in any case); the banner's own is fine. */
export const bannerNameRefusal = (
  banners: Banner[],
  name: string,
  ownId: string | null = null
): string | null =>
  banners.some((b) => b.id !== ownId && nameKey(b.name) === nameKey(name))
    ? BANNER_NAME_TAKEN
    : null

/**
 * A copy of the banner under new ids, for its groups too, named
 * "<name> (copy)", or "<name> (copy 2)" and on when that is taken.
 */
export const copyBanner = (
  banner: Banner,
  banners: Banner[],
  makeId: (prefix: "banner" | "group") => string = newTabSpecId
): Banner => {
  const base = banner.name.trim()
  let name = `${base} (copy)`
  for (let n = 2; bannerNameRefusal(banners, name); n++) name = `${base} (copy ${n})`
  return {
    id: makeId("banner"),
    name,
    groups: banner.groups.map((g) => ({
      ...g,
      id: makeId("group"),
      points: g.points ? g.points.map(copyPoint) : null,
    })),
  }
}
